// Used to post analytics events to Ahoy's events endpoint, several to a request. `Ahoy::EventsController`
// accepts an array of up to `Ahoy.max_events_per_request` events.
//
// The wire shape is `ahoy.js`'s own: a `FormData` body carrying `events_json`. The endpoint is served from
// `Sparkle.apiHost` rather than from the host the visitor is on, and multipart is one of the three content types
// a cross-origin POST may use without a preflight. `ahoy.js` owns the visit and visitor cookies.
//
// An event is `{id, name, properties, time}`, and `tokens` is the `{visit_token, visitor_token}` of the visit the
// events belong to.
//
// The first request sent while the page is in use, and the first after a rejected probe, goes out as a keepalive
// fetch rather than a beacon, as a probe of the endpoint: a beacon's response is never readable, and a fetch's is
// whenever the endpoint's CORS headers admit the page. An error response from the app, as `rejected` defines it, is
// passed to `onRejected`. A fetch that fails outright counts as sent, as a beacon would, because a CORS block fails
// the same way after the endpoint has recorded the events. A transport built with `probe: false` sends no probe.
//
// Decision record: https://github.com/combinaut/sparkle/wiki/ADR-0029-Batch-Analytics-Events-in-Our-Own-Modules-Around-a-Vendored-Ahoy-JS
export class AnalyticsEventTransport {
  // @param url {String|Function} the Ahoy events endpoint or a function that resolves it when sending
  // @param options {Object}
  //   - `eventsPerRequest`, `bytesPerRequest`: kept within `Ahoy.max_events_per_request` and `Ahoy.max_content_length`
  //   - `onRejected`: called with the events, the status and the tokens of a request the endpoint answered with an
  //     error
  //   - `probe`: false for a transport that sends no probe, and so sends every request as a beacon where the browser
  //     has `sendBeacon`. Defaults to true.
  constructor(url, options) {
    options = options || {};

    this.url = url;
    this.eventsPerRequest = options.eventsPerRequest || 10;
    this.bytesPerRequest = options.bytesPerRequest || 24576;
    this.onRejected = options.onRejected || function () {};
    this.probed = options.probe === false;
    this.probing = false;
  }

  // Returns the batches it did not send: those the browser refused, as it does once the beacons already in flight
  // fill its quota, and, until the probe has been answered, every batch but the probe, so none goes out as a beacon
  // before the probe's answer can be read. When `leaving` is true, a browser with `sendBeacon` sends every request as
  // a beacon, so a refusal is still reported.
  send(events, tokens, leaving) {
    const batches = this.batches(events);

    if (navigator.sendBeacon && !leaving && (this.probing || !this.probed)) {
      if (!this.probed) {
        this.post(batches.shift(), tokens, leaving);
      }

      return batches;
    }

    return batches.filter((batch) => !this.post(batch, tokens, leaving));
  }

  // Splits the events into requests the endpoint will accept. An event over the byte budget still goes out alone,
  // and the endpoint rejects it only if it also exceeds `Ahoy.max_content_length`.
  batches(events) {
    // The budget is in bytes because that is what the endpoint measures. A string's `length` counts UTF-16 code
    // units, so an accented or CJK `text` measures short of the bytes it sends.
    const encoder = new TextEncoder();
    const batches = [];
    let batch = [];
    let bytes = 0;

    events.forEach((event) => {
      const size = encoder.encode(JSON.stringify(event)).length;

      if (batch.length && (batch.length >= this.eventsPerRequest || bytes + size > this.bytesPerRequest)) {
        batches.push(batch);
        batch = [];
        bytes = 0;
      }

      batch.push(event);
      bytes += size;
    });

    if (batch.length) {
      batches.push(batch);
    }

    return batches;
  }

  // Returns whether the browser took the request. The probe, and every request from a browser without
  // `sendBeacon`, is a keepalive fetch, which outlives the page the same way a beacon does and always counts as taken.
  post(events, tokens, leaving) {
    const url = typeof this.url === "function" ? this.url() : this.url;

    if (navigator.sendBeacon && (this.probed || leaving)) {
      return navigator.sendBeacon(url, this.body(events, tokens));
    }

    this.probed = true;
    this.probing = true;

    fetch(url, { method: "POST", body: this.body(events, tokens), keepalive: true })
      .then((response) => {
        if (this.rejected(response.status)) {
          // The next send probes again, so the transport sends no beacon until a probe's answer has been read.
          this.probed = false;
          this.onRejected(events, response.status, tokens);
        }
      })
      .catch(function () {})
      .finally(() => {
        this.probing = false;
      });

    return true;
  }

  // Returns whether the status is an error the app itself returned: a 4xx or a 500. A 502, 503 or 504 comes from the
  // proxy in front of the app, which may have recorded the events before the proxy gave up on it.
  rejected(status) {
    return status >= 400 && status <= 500;
  }

  body(events, tokens) {
    const form = new FormData();
    const csrfParam = this.metaContent("csrf-param");
    const csrfToken = this.metaContent("csrf-token");

    form.append("visit_token", tokens.visit_token);
    form.append("visitor_token", tokens.visitor_token);
    form.append("events_json", JSON.stringify(events));

    if (csrfParam && csrfToken) {
      form.append(csrfParam, csrfToken);
    }

    return form;
  }

  metaContent(name) {
    const meta = document.querySelector("meta[name=" + name + "]");

    return meta && meta.content;
  }
}
